
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CTASection from "../components/CTASection";

import {
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  BedDouble,
  CalendarCheck2,
  CarFront,
  Check,
  ChevronRight,
  CircleHelp,
  Compass,
  Globe2,
  Headphones,
  Hotel,
  MapPin,
  Plane,
  ShieldCheck,
  Sparkles,
  Star,
  UtensilsCrossed,
  FileCheck2,
  PhoneCall,
  Luggage,
} from "lucide-react";

/* ============================================================
   SERVICES DATA
============================================================ */

const services = [
  {
    number: "01",
    title: "Hotel Booking",
    category: "ACCOMMODATION",
    description:
      "Discover comfortable hotels, stylish city stays, and memorable resorts with options for different travel budgets.",
    image:
      "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1200&q=85",
    icon: Hotel,
    link: "/",
    internal: true,
    featured: true,
  },
  {
    number: "02",
    title: "Flight Booking",
    category: "AIR TRAVEL",
    description:
      "Explore flight options for your next journey, whether you're travelling for business, leisure, or a family holiday.",
    image:
      "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=1200&q=85",
    icon: Plane,
    link: "https://southtravels.com/flights",
  },
  {
    number: "03",
    title: "Visa Services",
    category: "TRAVEL DOCUMENTS",
    description:
      "Access visa guidance and document assistance to help you prepare for your upcoming international trip.",
    image:
      "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=1200&q=85",
    icon: FileCheck2,
    link: "https://southtravels.com/",
  },
  {
    number: "04",
    title: "Tour Packages",
    category: "EXPERIENCES",
    description:
      "Explore Dubai's landmarks, unforgettable attractions, and exciting travel experiences for every kind of visitor.",
    image:
      "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=1200&q=85",
    icon: Compass,
    link: "https://tourismindubai.com/",
  },
  {
    number: "05",
    title: "Food & Dining",
    category: "DINING",
    description:
      "Discover dining experiences, local favourites, and memorable places to enjoy food during your Dubai visit.",
    image:
      "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1200&q=85",
    icon: UtensilsCrossed,
    link: "https://dubaicitytour.com/",
  },
  {
    number: "06",
    title: "Car Rentals",
    category: "TRANSPORTATION",
    description:
      "Explore convenient car rental options to travel around Dubai and discover the city at your own pace.",
    image:
      "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1200&q=85",
    icon: CarFront,
    link: "https://southtravels.com/car-rental-from-dubai",
  },
];

/* ============================================================
   BENEFITS
============================================================ */

const benefits = [
  {
    icon: BadgeCheck,
    title: "Great Travel Options",
    description:
      "Explore a range of accommodation and travel services to suit different plans and preferences.",
  },
  {
    icon: ShieldCheck,
    title: "Booking Confidence",
    description:
      "Review your options and important booking details before confirming your arrangements.",
  },
  {
    icon: Headphones,
    title: "Helpful Travel Support",
    description:
      "Get assistance with travel enquiries and planning your next journey.",
  },
  {
    icon: Globe2,
    title: "More Than Hotels",
    description:
      "Find accommodation, flights, transportation, tours, and other travel-related services.",
  },
];

/* ============================================================
   REUSABLE SERVICE LINK
============================================================ */

function ServiceLink({ service, children, className = "" }) {
  if (service.internal) {
    return (
      <Link to={service.link} className={className}>
        {children}
      </Link>
    );
  }

  return (
    <a
      href={service.link}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      {children}
    </a>
  );
}

/* ============================================================
   SERVICE CARD
============================================================ */

function ServiceCard({ service }) {
  const Icon = service.icon;

  return (
    <article className="group overflow-hidden rounded-[20px] border border-[#E3ECE6] bg-white shadow-[0_5px_25px_rgba(6,78,59,0.035)] transition-all duration-300 hover:-translate-y-1 hover:border-[#B9DDC7] hover:shadow-[0_18px_45px_rgba(6,78,59,0.10)]">
      <ServiceLink
        service={service}
        className="relative block h-[220px] overflow-hidden sm:h-[240px]"
      >
        <img
          src={service.image}
          alt={service.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-[#064E3B]/55 via-transparent to-transparent" />

        <span className="absolute left-5 top-5 rounded-full bg-white/95 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-wider text-[#17324D]">
          {service.category}
        </span>

        <span className="absolute bottom-5 left-5 flex h-12 w-12 items-center justify-center rounded-xl border border-white/30 bg-white text-[#168B43] shadow-md">
          <Icon size={23} strokeWidth={1.8} />
        </span>

        <span className="absolute bottom-5 right-5 flex h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-white/20 text-white backdrop-blur-md transition group-hover:bg-[#168B43]">
          <ArrowUpRight size={20} />
        </span>
      </ServiceLink>

      <div className="p-6">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-[#168B43]">
            Explore Service
          </p>
          <span className="text-sm font-extrabold text-[#D1E4D8]">
            {service.number}
          </span>
        </div>

        <h3 className="text-[23px] font-extrabold tracking-tight text-[#17324D]">
          {service.title}
        </h3>

        <p className="mt-3 min-h-[76px] text-[13px] leading-7 text-[#718096]">
          {service.description}
        </p>

        <ServiceLink
          service={service}
          className="mt-5 inline-flex items-center gap-2 text-[13px] font-extrabold text-[#168B43] transition-all group-hover:gap-3"
        >
          Explore Now
          <ArrowRight size={16} />
        </ServiceLink>
      </div>
    </article>
  );
}

/* ============================================================
   MAIN PAGE
============================================================ */

export default function Services() {
  return (
    <>
      <Helmet>
        <title>
          Our Services | Dubai Budget Hotels & Travel Services
        </title>

        <meta
          name="description"
          content="Explore hotel booking, flights, visa assistance, Dubai tour packages, dining and car rental services for your next journey."
        />

        <meta
          property="og:title"
          content="Our Services | Dubai Budget Hotels"
        />

        <meta
          property="og:description"
          content="Explore hotel stays, flights, visas, tours, dining and transportation services."
        />

        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <main className="min-h-screen overflow-hidden bg-white text-[#17324D]">

        {/* =====================================================
            HERO SECTION
        ===================================================== */}

        <section className="relative isolate min-h-[480px] overflow-hidden bg-[#064E3B] pt-24 sm:min-h-[530px]">
          <img
            src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=2000&q=90"
            alt="Dubai skyline"
            className="absolute inset-0 -z-20 h-full w-full object-cover object-center"
          />

          <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#032F28]/95 via-[#064E3B]/85 to-[#064E3B]/40" />

          <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#032F28]/35 to-transparent" />

          <div className="mx-auto flex min-h-[390px] w-full max-w-[1536px] flex-col justify-center px-4 py-16 sm:px-6 lg:px-8">
            <div className="max-w-[750px]">
              <div className="mb-6 flex items-center gap-2 text-xs font-semibold text-white/75">
                <Link to="/" className="transition hover:text-white">
                  Home
                </Link>
                <ChevronRight size={13} />
                <span className="text-[#B6F2CB]">
                  Our Services
                </span>
              </div>

              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#C5F4D4] backdrop-blur-md">
                <Sparkles size={15} />
                Discover Our Travel Services
              </div>

              <h1 className="max-w-[740px] text-[42px] font-extrabold leading-[1.12] tracking-[-0.045em] text-white sm:text-[58px] lg:text-[70px]">
                Everything You Need
                <br />
                For a{" "}
                <span className="text-[#A5E8B8]">
                  Perfect Trip.
                </span>
              </h1>

              <p className="mt-6 max-w-[600px] text-[15px] leading-8 text-white/80 sm:text-base">
                From finding your ideal Dubai hotel to exploring
                exciting destinations, discover travel services
                designed to make planning your next journey easier.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#our-services"
                  className="inline-flex items-center gap-3 rounded-xl bg-[#168B43] px-6 py-4 text-sm font-extrabold text-white shadow-[0_10px_25px_rgba(0,0,0,0.15)] transition hover:-translate-y-0.5 hover:bg-[#087D37]"
                >
                  Explore Our Services
                  <ArrowRight size={17} />
                </a>

                <Link
                  to="/"
                  className="inline-flex items-center gap-3 rounded-xl border border-white/35 bg-white/10 px-6 py-4 text-sm font-extrabold text-white backdrop-blur-md transition hover:bg-white/20"
                >
                  <BedDouble size={18} />
                  Find Hotels
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            INTRO HIGHLIGHTS
        ===================================================== */}



        {/* =====================================================
            SERVICES GRID
        ===================================================== */}

        <section
          id="our-services"
          className="scroll-mt-24 bg-[#F5FAF7] py-20 sm:py-24"
        >
          <div className="mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">

            <div className="mb-11 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <span className="h-[2px] w-8 bg-[#168B43]" />
                  <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#168B43]">
                    What We Offer
                  </p>
                </div>

                <h2 className="text-[34px] font-extrabold leading-tight tracking-[-0.04em] text-[#17324D] sm:text-[44px]">
                  Travel Services Designed
                  <br className="hidden sm:block" />
                  {" "}Around You
                </h2>

                <p className="mt-4 max-w-[600px] text-sm leading-7 text-[#718096]">
                  Whether you're booking a hotel, arranging a flight,
                  or exploring Dubai, discover our range of travel
                  services in one convenient place.
                </p>
              </div>

              <div className="inline-flex w-fit items-center gap-3 rounded-xl border border-[#DCEAE1] bg-white px-5 py-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#E8F7EE] text-[#168B43]">
                  <Luggage size={20} />
                </div>
                <div>
                  <p className="text-sm font-extrabold text-[#17324D]">
                    6 Travel Services
                  </p>
                  <p className="text-[11px] text-[#8492A6]">
                    Explore your options
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {services.map((service) => (
                <ServiceCard
                  key={service.title}
                  service={service}
                />
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================
            HOTEL FEATURE SECTION
        ===================================================== */}

        <section className="bg-white py-20 sm:py-24">
          <div className="mx-auto grid w-full max-w-[1536px] items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
            <div className="relative">
              <div className="relative overflow-hidden rounded-[24px]">
                <img
                  src="https://images.unsplash.com/photo-1571896349842-33c89424de2d?auto=format&fit=crop&w=1400&q=85"
                  alt="Beautiful hotel resort pool"
                  loading="lazy"
                  className="h-[350px] w-full object-cover sm:h-[500px]"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-[#064E3B]/40 to-transparent" />
              </div>

              <div className="absolute -bottom-6 left-5 flex max-w-[260px] items-center gap-4 rounded-2xl border border-[#E6EFE9] bg-white p-5 shadow-[0_15px_40px_rgba(6,78,59,0.14)] sm:left-8">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                  <BedDouble size={23} />
                </div>

                <div>
                  <p className="text-sm font-extrabold text-[#17324D]">
                    Your Ideal Stay
                  </p>
                  <p className="mt-1 text-xs text-[#8492A6]">
                    Explore Dubai hotels
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-7 lg:pt-0">
              <div className="mb-4 flex items-center gap-2">
                <span className="h-[2px] w-8 bg-[#168B43]" />
                <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#168B43]">
                  Find Your Perfect Stay
                </p>
              </div>

              <h2 className="max-w-[620px] text-[35px] font-extrabold leading-[1.17] tracking-[-0.04em] text-[#17324D] sm:text-[49px]">
                Great Dubai Trips
                <br />
                Begin With a{" "}
                <span className="text-[#168B43]">
                  Great Hotel.
                </span>
              </h2>

              <p className="mt-6 max-w-[560px] text-sm leading-8 text-[#718096]">
                Explore comfortable accommodation across Dubai,
                from city-centre hotels to relaxing resort stays.
                Discover options that fit your plans, preferences,
                and budget.
              </p>

              <div className="mt-7 space-y-4">
                {[
                  "Explore hotels across popular Dubai locations",
                  "Discover accommodation for different budgets",
                  "Review room options and booking details",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#E8F7EE] text-[#168B43]">
                      <Check size={15} strokeWidth={3} />
                    </span>
                    <span className="text-sm font-semibold text-[#425466]">
                      {item}
                    </span>
                  </div>
                ))}
              </div>

              <Link
                to="/"
                className="mt-9 inline-flex items-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-sm font-extrabold text-white transition hover:-translate-y-0.5 hover:bg-[#087D37]"
              >
                Explore Dubai Hotels
                <ArrowRight size={17} />
              </Link>
            </div>
          </div>
        </section>

        {/* =====================================================
            WHY CHOOSE US
        ===================================================== */}

        <section className="bg-[#F5FAF7] py-20 sm:py-24">
          <div className="mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-[720px] text-center">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#168B43]">
                Why Choose Us
              </p>

              <h2 className="mt-3 text-[34px] font-extrabold leading-tight tracking-[-0.04em] text-[#17324D] sm:text-[44px]">
                Travel Planning Made
                <span className="text-[#168B43]"> Easier</span>
              </h2>

              <p className="mx-auto mt-4 max-w-[580px] text-sm leading-7 text-[#718096]">
                We bring together useful travel services to help
                you plan accommodation, transportation, and
                memorable experiences.
              </p>
            </div>

            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {benefits.map(({ icon: Icon, title, description }) => (
                <div
                  key={title}
                  className="group rounded-[18px] border border-[#E2ECE6] bg-white p-7 transition hover:-translate-y-1 hover:border-[#B9DDC7] hover:shadow-[0_15px_35px_rgba(6,78,59,0.08)]"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43] transition group-hover:bg-[#168B43] group-hover:text-white">
                    <Icon size={25} strokeWidth={1.8} />
                  </div>

                  <h3 className="mt-6 text-[17px] font-extrabold text-[#17324D]">
                    {title}
                  </h3>

                  <p className="mt-3 text-[13px] leading-7 text-[#718096]">
                    {description}
                  </p>

                  <div className="mt-6 h-[3px] w-10 rounded-full bg-[#168B43]" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================
            TRAVEL INSPIRATION BANNER
        ===================================================== */}

        <section className="bg-white py-16 sm:py-20">
          <div className="mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">
            <div className="relative isolate overflow-hidden rounded-[24px] bg-[#064E3B] px-6 py-14 text-center sm:px-12 sm:py-20">
              <img
                src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=1800&q=85"
                alt=""
                loading="lazy"
                className="absolute inset-0 -z-20 h-full w-full object-cover"
              />

              <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#032F28]/95 via-[#064E3B]/90 to-[#064E3B]/75" />

              <div className="mx-auto max-w-[760px]">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-white/25 bg-white/10 text-[#B6F2CB]">
                  <Globe2 size={27} />
                </div>

                <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#B6F2CB]">
                  Your Journey Starts Here
                </p>

                <h2 className="mt-4 text-[33px] font-extrabold leading-tight tracking-[-0.035em] !text-white sm:text-[49px]">
                  Ready to Explore Dubai?
                </h2>

                <p className="mx-auto mt-5 max-w-[570px] text-sm leading-8 text-white/75">
                  Discover hotels, explore new experiences, and start
                  planning your next memorable trip.
                </p>

                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-sm font-extrabold text-white transition hover:bg-[#087D37]"
                  >
                    Discover Hotels
                    <ArrowRight size={17} />
                  </Link>

                  <a
                    href="#our-services"
                    className="inline-flex items-center gap-3 rounded-xl border border-white/30 bg-white/10 px-7 py-4 text-sm font-extrabold text-white backdrop-blur-md transition hover:bg-white/20"
                  >
                    Browse Services
                    <ChevronRight size={17} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            CONTACT SECTION
        ===================================================== */}

        <section className="border-t border-[#E6EFE9] bg-[#F5FAF7] py-12">
          <div className="mx-auto flex w-full max-w-[1536px] flex-col items-start justify-between gap-7 px-4 sm:px-6 lg:flex-row lg:items-center lg:px-8">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-[#E1F3E8] text-[#168B43]">
                <Headphones size={27} />
              </div>

              <div>
                <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#168B43]">
                  Need Assistance?
                </p>

                <h2 className="mt-2 text-[25px] font-extrabold tracking-tight text-[#17324D] sm:text-[30px]">
                  Have Questions About Your Trip?
                </h2>

                <p className="mt-2 text-sm text-[#718096]">
                  Contact our travel team for enquiries and assistance.
                </p>
              </div>
            </div>

            <a
              href="tel:+97144473839"
              className="inline-flex shrink-0 items-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-sm font-extrabold text-white transition hover:bg-[#087D37]"
            >
              <PhoneCall size={18} />
              +971 4 447 3839
              <ArrowRight size={16} />
            </a>
          </div>
        </section>

        {/* EXISTING CTA COMPONENT */}
        <CTASection />
      </main>
    </>
  );
}
